import React from 'react';
import { Layout as AntLayout, Breadcrumb, Tag, Button, Tooltip } from 'antd';
import {
  HomeOutlined,
  FileTextOutlined,
  BarChartOutlined,
  ExperimentOutlined,
  MoonOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, useSearchParams, Outlet } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const { Header, Content } = AntLayout;

interface NavItem {
  key: string;
  icon: React.ReactNode;
  label: string;
}

const navItems: NavItem[] = [
  { key: '/', icon: <HomeOutlined />, label: '首页' },
  { key: '/templates', icon: <FileTextOutlined />, label: '数据模板' },
  { key: '/smart-chart/workspace', icon: <BarChartOutlined />, label: '智能图表' },
  { key: '/analysis/workspace', icon: <ExperimentOutlined />, label: '统计分析' },
];

const breadcrumbMap: Record<string, string> = {
  '/': '首页',
  '/templates': '数据模板',
  '/templates/preview': '模板预览',
  '/smart-chart': '智能图表',
  '/smart-chart/workspace': '工作台',
  '/smart-chart/upload': '上传数据',
  '/smart-chart/data-select': '数据选择',
  '/smart-chart/type-select': '图表选择',
  '/smart-chart/result': '分析结果',
  '/analysis': '统计分析',
  '/analysis/workspace': '工作台',
  '/analysis/upload': '上传数据',
  '/analysis/data-select': '数据选择',
  '/analysis/type-select': '分析选择',
  '/analysis/result': '分析结果',
};

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isDark, toggleTheme } = useTheme();

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbItems = [
    { title: <span onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>首页</span> },
    ...pathSegments.map((_, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      const name = breadcrumbMap[path] || pathSegments[index];
      return { title: name };
    }),
  ];

  const currentPath = location.pathname;
  let activeKey = '/';
  for (const item of navItems) {
    if (item.key !== '/' && currentPath.startsWith(item.key.split('/').slice(0, 2).join('/'))) {
      activeKey = item.key;
      break;
    }
  }

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: isDark ? '#0f1629' : '#061b31',
        padding: '0 28px',
        display: 'flex',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        height: 56,
        borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : 'none',
      }}>
        {/* Logo */}
        <div
          onClick={() => navigate('/')}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            cursor: 'pointer', marginRight: 36, flexShrink: 0,
          }}
        >
          <div style={{
            width: 30, height: 30, borderRadius: 6,
            background: '#533afd',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 15, fontWeight: 700,
            boxShadow: '0 2px 8px rgba(83, 58, 253, 0.4)',
          }}>t</div>
          <span style={{
            fontSize: 17, fontWeight: 700, letterSpacing: -0.3,
            color: '#ffffff',
          }}>trendata</span>
        </div>

        {/* Nav items */}
        <nav style={{ display: 'flex', gap: 4, flex: 1 }}>
          {navItems.map((item) => {
            const isActive = item.key === activeKey;
            return (
              <button
                key={item.key}
                onClick={() => {
                  // Pass fileId/fileName across workspace navigations
                  const fid = searchParams.get('fileId');
                  const fname = searchParams.get('fileName');
                  const isWorkspace = item.key.includes('/workspace');
                  if (isWorkspace && fid) {
                    const params = new URLSearchParams();
                    params.set('fileId', fid);
                    if (fname) params.set('fileName', fname);
                    navigate(`${item.key}?${params.toString()}`);
                  } else {
                    navigate(item.key);
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#ffffff' : 'rgba(255,255,255,0.6)',
                  background: isActive ? 'rgba(83, 58, 253, 0.25)' : 'transparent',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {item.icon}
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tooltip title={isDark ? '切换浅色模式' : '切换深色模式'}>
            <Button
              type="text"
              icon={isDark ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleTheme}
              style={{
                fontSize: 14,
                color: isDark ? '#fbbf24' : 'rgba(255,255,255,0.6)',
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            />
          </Tooltip>
          <Tag style={{
            fontSize: 10,
            color: '#b9b9f9',
            background: 'rgba(83, 58, 253, 0.2)',
            border: '1px solid rgba(83, 58, 253, 0.3)',
            borderRadius: 9999,
          }}>Beta</Tag>
        </div>
      </Header>

      <Content style={{ padding: '0 24px', marginTop: 16 }}>
        {location.pathname !== '/' && pathSegments.length > 1 && (
          <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 12, fontSize: 12 }} />
        )}
        <div style={{ minHeight: 'calc(100vh - 100px)' }}>
          <Outlet />
        </div>
      </Content>
    </AntLayout>
  );
};

export default AppLayout;
